/**
 * Status Graph Component
 *
 * Migrated from: lego/page/etmf/overview/status-graph/StatusGraph.jsx
 *
 * Renders a donut chart showing document completion status across ETMF hierarchy levels.
 * Displays counts for: Not Started, In Progress, and Completed documents.
 *
 * Uses Chart.js library for rendering.
 */

import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cloneDeep from 'lodash/cloneDeep'
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js'
import type { StatusGraphProps, GraphData } from '../types'

// Register Chart.js components
Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

// Label colors for chart segments
const LABEL_COLORS = {
  MISSING: '#D1D8E0',
  IN_PROCESS: '#F2BE4A',
  COMPLETE: '#37BE87'
} as const

/**
 * StatusGraph displays a donut chart with document completion statistics
 *
 * @param props.data - Array of [notStarted, inProgress, completed] counts
 * @param props.filter - Filter context (product, trial, environment, country, site)
 * @param props.onStatsClick - Callback when a legend item is clicked
 */
const StatusGraph: React.FC<StatusGraphProps> = ({ data, filter, onStatsClick }) => {
  const { t } = useTranslation()
  const [chart, setChart] = useState<any>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    drawGraph()

    // Cleanup function to destroy chart on unmount
    return () => {
      if (chart?.destroy) {
        chart.destroy()
      }
    }
  }, [data])

  const drawGraph = () => {
    // Destroy existing chart before creating new one
    if (chart?.destroy) {
      chart.destroy()
    }

    const graphData: GraphData = cloneDeep(data)
    const canvas = canvasRef.current

    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Create Chart.js donut chart
    const chartInstance = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [
          t('ETMF_GRAPHS_LABEL_NOT_STARTED'),
          t('ETMF_GRAPHS_LABEL_IN_PROGRESS'),
          t('ETMF_GRAPHS_LABEL_COMPLETE')
        ],
        datasets: [
          {
            label: '',
            data: graphData,
            backgroundColor: [LABEL_COLORS.MISSING, LABEL_COLORS.IN_PROCESS, LABEL_COLORS.COMPLETE]
          }
        ]
      },
      options: {
        plugins: {
          tooltip: {
            enabled: false
          },
          legend: {
            display: false,
            position: 'bottom',
            labels: {
              color: '#333'
            }
          }
        }
      }
    })

    setChart(chartInstance)
  }

  const totalDocs = data[0] + data[1] + data[2]
  const hasNoProduct = !filter?.product
  const hasNoData = totalDocs === 0
  const shouldHideLegend = hasNoProduct || hasNoData

  return (
    <div className={`chart-custom-wrapper ${shouldHideLegend ? 'notShowLegendOnHover' : ''}`}>
      {/* Placeholder when no product selected */}
      {hasNoProduct && (
        <div className="placeholder">
          <div className="placeholder-chart"></div>
          <div className="placeholder-text">{t('ETMF_GRAPHS_SELECT_PRODUCT_BELOW')}</div>
        </div>
      )}

      <div>
        {/* Placeholder when no data available */}
        {hasNoData && (
          <div className="placeholder">
            <div className="placeholder-chart"></div>
            <div className="placeholder-text">{t('ETMF_GRAPHS_NO_DATA')}</div>
          </div>
        )}

        {/* Chart canvas */}
        <canvas ref={canvasRef} id={`canvas-${filter?.type}`} height="150" width="150" />

        {/* Total documents display */}
        <div className="total-docs">
          <span>{totalDocs}</span>
          {t('DOCUMENTS')?.toLowerCase()}
        </div>

        {/* Legend with clickable items */}
        <div className="chartjsLegend">
          <ul className="6-legend">
            <li onClick={() => onStatsClick('NOT_STARTED', filter)}>
              <span className="color-square" style={{ backgroundColor: LABEL_COLORS.MISSING }} />
              <strong>{data[0]} </strong>
              {t('ETMF_GRAPHS_LABEL_NOT_STARTED')}
            </li>
            <li onClick={() => onStatsClick('IN_PROGRESS', filter)}>
              <span className="color-square" style={{ backgroundColor: LABEL_COLORS.IN_PROCESS }} />
              <strong>{data[1]} </strong>
              {t('ETMF_GRAPHS_LABEL_IN_PROGRESS')}
            </li>
            <li onClick={() => onStatsClick('COMPLETED', filter)}>
              <span className="color-square" style={{ backgroundColor: LABEL_COLORS.COMPLETE }} />
              <strong>{data[2]} </strong>
              {t('ETMF_GRAPHS_LABEL_COMPLETE')}
            </li>
          </ul>
          <strong>
            {t('ETMF_GRAPHS_TOTAL')}: {totalDocs}
          </strong>
        </div>
      </div>
    </div>
  )
}

export default StatusGraph
